import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

interface UserHeadingConeProps {
  position: [number, number];
  // Screen-relative rotation in degrees (0 = pointing toward the top of the screen).
  rotation: number;
}

// Renders a translucent direction beam under the user's location dot that points
// where the device is facing. Lives in a dedicated low-z pane so it sits beneath
// the blue dot. Rotation is applied imperatively to avoid recreating the icon.
export function UserHeadingCone({ position, rotation }: UserHeadingConeProps) {
  const map = useMap();
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!map.getPane("userHeading")) {
      // Parent under the NON-ROTATING marker pane group (markerPane's parent =
      // norotatePane) so the cone shares the same coordinate space and upright
      // behaviour as the blue "you" dot when the map is rotated. z-index just
      // below the default markerPane (600) keeps the cone beneath the dot.
      const markerParent =
        map.getPane("markerPane")?.parentElement ?? undefined;
      const pane = map.createPane(
        "userHeading",
        markerParent as HTMLElement | undefined,
      );
      pane.style.zIndex = "590";
      pane.style.pointerEvents = "none";
    }
    const icon = L.divIcon({
      className: "uhc-icon",
      html: '<div class="uhc-rotor"><div class="uhc-beam"></div></div>',
      iconSize: [80, 80],
      iconAnchor: [40, 40],
    });
    const marker = L.marker(position, {
      icon,
      interactive: false,
      keyboard: false,
      pane: "userHeading",
    });
    marker.addTo(map);
    markerRef.current = marker;
    return () => {
      marker.remove();
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    markerRef.current?.setLatLng(position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position[0], position[1]]);

  useEffect(() => {
    const el = markerRef.current?.getElement();
    const rotor = el?.querySelector(".uhc-rotor") as HTMLElement | null;
    if (rotor) rotor.style.transform = `rotate(${rotation}deg)`;
  }, [rotation]);

  return null;
}
